import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
    MatAutocomplete,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger,
    MatOption,
} from '@angular/material/autocomplete';
import { filter, isEqual } from 'lodash-es';
import { MatFormField, MatFormFieldModule, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatChipInput, MatChipsModule } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { BaseFieldComponent, TooltipTextComponent } from 'vcp-common-ui-lib';

/** Example of chips autocomplete usage
 * HTML
 * <vc-common-chips-autocomplete label="Chips Autocomplete"
 *     [(values)]="selectedChips"
 *     [required]="true"
 *     [options]="chipsOptions"
 *     [itemRenderer]="chipsAutocompleteItemRenderer"></vc-common-chips-autocomplete>
 *
 * TS
 *  chipsOptions: { position: number; name: string }[] = [
 *    { position: 1, name: 'Hydrogen' },
 *    { position: 2, name: 'Helium' },
 *     { position: 3, name: 'Lithium' },
 *  ];
 *
 *  selectedChips: { position: number; name: string }[] = [this.chipsOptions[0]];
 *
 *  public chipsAutocompleteItemRenderer = (value: { position: number; name: string }) => value?.name ?? '';
 *
 * */

@Component({
    selector: 'vc-common-chips-autocomplete',
    standalone: true,
    imports: [
        MatChipsModule,
        MatLabel,
        TooltipTextComponent,
        MatIcon,
        FormsModule,
        MatAutocompleteTrigger,
        MatChipInput,
        MatAutocomplete,
        MatSuffix,
        MatOption,
        MatFormField,
        MatFormFieldModule,
    ],
    templateUrl: './common-chips-autocomplete.component.html',
    styleUrl: './common-chips-autocomplete.component.scss',
})
export class CommonChipsAutocompleteComponent<T> extends BaseFieldComponent<T> {
    filteredOptions!: T[];
    separatorKeysCodes: number[] = [ENTER, COMMA];
    filterValue: string = '';
    private _filterChangeTimer!: NodeJS.Timeout;

    @Input()
    set values(val: T[]) {
        if (val != this._values) {
            this._values = val;
            this._setAutocompleteItems();
            this.valuesChange.emit(this._values);
            this.validate();
        }
    }

    get values(): T[] {
        return this._values;
    }

    private _values: T[] = [];

    /** List of options for chips autocomplete */
    @Input()
    set options(value: T[]) {
        this._options = value;
        this.filteredOptions = this._options;
        this._setAutocompleteItems();
    }

    get options(): T[] {
        return this._options;
    }

    private _options: T[] = [];

    /** Whether multiple chips can be selected */
    @Input()
    multiselect: boolean = true;

    /** Whether chip can be deleted */
    @Input()
    canDeleteChip: boolean = true;

    /** Autocomplete placeholder value */
    @Input()
    placeholder!: string;

    /** Identifies the element (or elements) that describes the autocomplete. */
    @Input()
    ariaDescribedBy!: string;

    /** Suffix icon. */
    @Input()
    suffixIcon!: string;

    /** Function for rendering options list and selected values */
    @Input()
    prefixIconRenderer!: (data: T) => string;

    /** Whether to allow duplicate chips selection */
    @Input()
    allowDuplicates: boolean = false;

    /** Whether to use outline on the prefix icon */
    @Input()
    prefixIconUseOutline: boolean = true;

    /** Function for rendering options list and selected values */
    @Input()
    itemRenderer: (data: T) => string = (value: T): string => `${value}`;

    /** Debounce interval for filter change. */
    @Input()
    filterDebounceInterval: number = 500;

    /** Manually set the width of the dropdown panel; Default sizing is based on the width of the input field. */
    @Input()
    panelWidth!: string | number;

    /** Maximum allowed selected values; selection beyond this limit is allowed but invalidates the field. */
    @Input()
    maximumValues?: number;

    /** Error message when selected values exceed maximumValues. */
    @Input()
    maximumValuesErrorMessage?: string;

    /** Two ways data binding for autocomplete value */
    @Output()
    valuesChange: EventEmitter<T[]> = new EventEmitter<T[]>();

    /** Two ways data binding for filter value */
    @Output()
    filterChange: EventEmitter<string> = new EventEmitter<string>();

    @ViewChild('autocompleteInput')
    autocompleteInput!: ElementRef<HTMLInputElement>;

    @ViewChild(MatAutocompleteTrigger)
    autocomplete!: MatAutocompleteTrigger;

    remove(chipData: T, index: number): void {
        if (this.disabled || this.readonly) {
            return;
        }
        if (this.allowDuplicates) {
            if (index >= 0) {
                const newValues = [...this.values];
                newValues.splice(index, 1);
                this.values = newValues;
            }
        } else {
            this.values = filter(this.values, (value: T) => !isEqual(chipData, value));
            this.filteredOptions = [...this.filteredOptions, chipData];
        }
        this.autocomplete.closePanel();
    }

    selectValue(event: MatAutocompleteSelectedEvent): void {
        if (!this.multiselect) {
            this.values = [event.option.value];
        } else {
            this.values = [...this.values, event.option.value];
        }

        this.autocompleteInput.nativeElement.value = '';
    }

    inputValueChanges(): void {
        // Filter items only when user typing
        if (typeof this.filterValue === 'string' || typeof this.filterValue === 'number') {
            this.filteredOptions = this._filter(this.filterValue);
        }
        this._emitFilterChange();
    }

    /** Validation function */
    override validate(): boolean {
        this.valid = true;
        if (this.required && this._values.length == 0) {
            this.errorMessage = `${this.label ?? this.fieldText} ${this.requiredText}`;
            this.valid = false;
        }

        if (this.maximumValues != null && this._values.length > this.maximumValues) {
            this.errorMessage = this.maximumValuesErrorMessage ?? '';
            this.valid = false;
            this.touched = true;
        }

        if (this.validationFunction != null && this.valid) {
            this.errorMessage = this.validationFunction(this._values);
            this.valid = this.errorMessage == '';
        }

        this.errorMessage = this.valid ? '' : this.errorMessage;

        return this.valid;
    }

    override clear(): void {
        if (!this.readonly && !this.disabled) {
            this.values = [];
        }
        this.filterValue = '';
        this.filterChange.emit(this.filterValue);
        this.filteredOptions = this.options;
        this.valid = true;
        this.touched = false;
    }

    private _setAutocompleteItems(): void {
        if (!this.allowDuplicates) {
            this.filteredOptions = filter(this.filteredOptions, (value: T) => !this._values?.includes(value));
        }
    }

    private _filter(value: string | number): T[] {
        const filterValue: string = value?.toString()?.toLowerCase();
        return this.options?.filter(
            (option: T) =>
                (this.itemRenderer(option) ?? '').toString()?.toLowerCase()?.includes(filterValue) &&
                (this.allowDuplicates || !this._values.includes(option))
        );
    }

    private _emitFilterChange(): void {
        clearTimeout(this._filterChangeTimer);
        this._filterChangeTimer = setTimeout((): void => {
            if (typeof this.filterValue === 'string' || typeof this.filterValue === 'number') {
                this.filterChange.emit(this.filterValue.toString());
            }
        }, this.filterDebounceInterval);
    }
}
